import type { Root } from './types';
import { useDebounce } from '@simplywallst/hooks/useDebounce';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getAlgoliaIndex } from './utils';
import { useDeferredValue } from 'react';

export const filterHits = (hits: Root[], include: Root['type'][]): Root[] => {
  return hits.filter((result) => {
    return include.includes(result.type);
  });
};

export const getQueryKey = (query: string) =>
  ['ALGOLIA_SEARCH', query] as const;

type Args = Parameters<typeof getAlgoliaIndex>[0] & {
  query?: string;
  include?: Root['type'][];
  keepPrevData?: boolean;
};

export const useAlgoliaSearch = ({
  algoliaAppId,
  algoliaApiKey,
  index,
  query = '',
  include = ['company'],
  keepPrevData = false,
}: Args) => {
  const searchIndex = getAlgoliaIndex({ algoliaAppId, algoliaApiKey, index });
  const deferredQuery = useDeferredValue(query);
  const debouncedQuery = useDebounce(deferredQuery, 420);

  const {
    data = [],
    status,
    isFetching,
  } = useQuery({
    queryKey: getQueryKey(debouncedQuery),
    queryFn: async ({ queryKey }) => {
      const { 1: q } = queryKey;
      const response = await searchIndex.search<Root>(q, {
        highlightPostTag: ' ',
        highlightPreTag: ' ',
        restrictHighlightAndSnippetArrays: true,
      });
      if (typeof response !== 'undefined') {
        return filterHits(response.hits, include);
      }
      return [];
    },
    placeholderData: keepPrevData ? keepPreviousData : undefined,
    enabled: debouncedQuery !== '',
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 3600,
  });

  return {
    data,
    status,
    isFetching,
  };
};

export type AlgoliaSearchResults = NonNullable<
  ReturnType<typeof useAlgoliaSearch>['data']
>;

export type AlgoliaSearchResult = AlgoliaSearchResults[number];
